import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import SketchViewModel from '@arcgis/core/widgets/Sketch/SketchViewModel';
import type { ArcgisMapView } from './arcgisView';

type UseArcgisSketchLayerArgs = {
  mapView: ArcgisMapView | null;
  layerTitle: string;
  /**
   * Also fires on each polygon vertex; `isFinished` is true only on the final
   * shape.
   */
  onCreateComplete?: (args: {
    geometry: __esri.GeometryUnion;
    sketchViewModel: SketchViewModel;
    isFinished: boolean;
  }) => void;
  onUpdateComplete?: (args: {
    geometry: __esri.GeometryUnion;
    sketchViewModel: SketchViewModel;
  }) => void;
  onCreateCancel?: () => void;
};

/**
 * Manages a layer for drawn objects on the map, and a sketch view model for the
 * drawing operations.
 */
export const useArcgisSketchLayer = ({
  mapView,
  layerTitle,
  onCreateComplete,
  onUpdateComplete,
  onCreateCancel,
}: UseArcgisSketchLayerArgs) => {
  const onCreateCompleteRef = useRef(onCreateComplete);
  const onUpdateCompleteRef = useRef(onUpdateComplete);
  const onCreateCancelRef = useRef(onCreateCancel);
  const drawingLayerRef = useRef<GraphicsLayer>(
    new GraphicsLayer({ title: layerTitle })
  );
  const sketchViewModelRef = useRef<SketchViewModel | null>(null);
  const [isSketchReady, setIsSketchReady] = useState(false);

  useLayoutEffect(() => {
    onCreateCompleteRef.current = onCreateComplete;
  }, [onCreateComplete]);

  useLayoutEffect(() => {
    onUpdateCompleteRef.current = onUpdateComplete;
  }, [onUpdateComplete]);

  useLayoutEffect(() => {
    onCreateCancelRef.current = onCreateCancel;
  }, [onCreateCancel]);

  useEffect(() => {
    if (!mapView?.map) {
      setIsSketchReady(false);
      return;
    }

    const map = mapView.map;
    const drawingLayer = drawingLayerRef.current;
    map.add(drawingLayer);

    const sketchViewModel = new SketchViewModel({
      view: mapView,
      layer: drawingLayer,
      updateOnGraphicClick: false,
    });
    sketchViewModelRef.current = sketchViewModel;
    setIsSketchReady(true);
    const createHandle = sketchViewModel.on('create', (event) => {
      const infoType =
        event.toolEventInfo && 'type' in event.toolEventInfo
          ? event.toolEventInfo.type
          : null;
      const geometry = event.graphic?.geometry;
      const isPolygonVertex =
        geometry?.type === 'polygon' && infoType === 'vertex-add';
      if (event.state === 'cancel') {
        drawingLayer.removeAll();
        onCreateCancelRef.current?.();
        return;
      }
      if (
        (event.state !== 'complete' && !isPolygonVertex) ||
        !event.graphic?.geometry
      ) {
        return;
      }

      onCreateCompleteRef.current?.({
        geometry: event.graphic.geometry,
        sketchViewModel,
        isFinished: event.state === 'complete',
      });
    });

    const updateHandle = sketchViewModel.on('update', (event) => {
      if (event.state !== 'complete') return;
      const geometry = event.graphics[0]?.geometry;
      if (!geometry) return;
      onUpdateCompleteRef.current?.({ geometry, sketchViewModel });
    });

    return () => {
      createHandle.remove();
      updateHandle.remove();
      try {
        sketchViewModel.cancel();
      } catch {
        // idle
      }
      sketchViewModel.destroy();
      sketchViewModelRef.current = null;
      setIsSketchReady(false);
      map.remove(drawingLayer);
    };
  }, [mapView]);

  const clearDrawingLayer = useCallback(() => {
    drawingLayerRef.current.removeAll();
  }, []);

  const cancelActiveDrawing = useCallback(() => {
    const sketchViewModel = sketchViewModelRef.current;
    if (!sketchViewModel) return;
    try {
      sketchViewModel.cancel();
    } catch {
      // SketchViewModel.cancel throws when no create/update is in progress.
    }
  }, []);

  return {
    drawingLayerRef,
    sketchViewModelRef,
    isSketchReady,
    clearDrawingLayer,
    cancelActiveDrawing,
  };
};
